import { useQuery, useMutation } from '@apollo/client';

import {
  RewardConfiguration,
  ConfigureRewardsMutation,
  ConfigureRewardsMutationVariables,
  GetCompanyRewardsConfigurationQuery
} from 'graphql.types';
import {
  RewardTier,
  RewardTierConfiguration,
  SnappyIntegrationConfig
} from 'settings/Settings.types';

import { configureRewards } from 'settings/graphql/configureRewards';
import { getCompanyRewardsConfiguration } from 'settings/graphql/getCompanyRewardsConfiguration';

export type ClearedDates = {
  low: string;
  mid: string;
  high: string;
};

type RewardsFormValues = {
  fiveStarRewards: boolean;
  thresholdEnabled: boolean;
  rewards: RewardTierConfiguration[];
  snappyIntegration: SnappyIntegrationConfig;
  clearedAtByTier?: ClearedDates;
};

type RewardsResponse = RewardsFormValues & {
  loading: boolean;
  saving: boolean;
  save: (newConfiguration: RewardsFormValues) => Promise<boolean | undefined>;
};

function getRewardConfiguration(
  rewards: RewardTierConfiguration[],
  tier: RewardTier
): RewardConfiguration {
  const rewardForTier =
    rewards.find(reward => reward.tier === tier) ||
    ({} as RewardTierConfiguration);

  return {
    reward: rewardForTier.id,
    threshold: rewardForTier.threshold ?? undefined,
    campaignId: rewardForTier.campaignId ?? undefined,
    secondaryCampaignId: rewardForTier.secondaryCampaignId ?? undefined,
  };
}

export function useRewards(): RewardsResponse {
  const { data, loading } = useQuery<GetCompanyRewardsConfigurationQuery>(
    getCompanyRewardsConfiguration
  );
  const [saveConfiguration, { loading: saving }] = useMutation<
    ConfigureRewardsMutation,
    ConfigureRewardsMutationVariables
  >(configureRewards);

  const handleSave = async (newConfiguration: RewardsFormValues) => {
    const { rewards } = newConfiguration;
    const rewardsByTier = {
      lowTier: getRewardConfiguration(rewards, RewardTier.Low),
      midTier: getRewardConfiguration(rewards, RewardTier.Mid),
      highTier: getRewardConfiguration(rewards, RewardTier.High),
    };

    return saveConfiguration({
      variables: {
        rewardConfiguration: rewardsByTier,
        thresholdsEnabled: newConfiguration.thresholdEnabled,
        fiveStarRewards: newConfiguration.fiveStarRewards,
      },
    }).then(response => response.data?.configureRewards);
  };

  return {
    saving,
    loading,
    save: handleSave,
    rewards: data?.company?.rewards || [],
    thresholdEnabled: Boolean(data?.company?.thresholdsEnabled),
    clearedAtByTier: data?.company?.clearedAtByTier,
    fiveStarRewards: Boolean(data?.company?.fiveStarRewards),
    snappyIntegration: {
      active: data?.snappy.enabled || false,
      apiKey: data?.snappy.apiKey || null,
    },
  };
}
